import React, { useState } from 'react';

const MONTHLY_PRICE_ID = 'price_1TDCJKRta59KZz9Uj6SwbNnE';
const YEARLY_PRICE_ID  = 'price_1TDCJLRta59KZz9UH4fWGh2w';

async function startCheckout(priceId, email) {
  const res = await fetch('/.netlify/functions/create-checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ priceId, email }),
  });
  const data = await res.json();
  if (data.url) window.location.href = data.url;
  else alert('Fehler: ' + (data.error || 'Unbekannt'));
}

function CheckoutButton({ priceId, children, variant = 'primary' }) {
  const [loading, setLoading] = useState(false);

  const handle = async () => {
    setLoading(true);
    await startCheckout(priceId);
    setLoading(false);
  };

  const base = 'w-full py-3 rounded-2xl text-sm font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-60';
  const styles = variant === 'primary'
    ? `${base} bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-200`
    : `${base} bg-white text-indigo-600 border-2 border-indigo-200 hover:border-indigo-400`;

  return (
    <button onClick={handle} disabled={loading} className={styles}>
      {loading && <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>}
      {children}
    </button>
  );
}

const features = [
  { icon: '✍️', title: 'Skript-Editor', desc: 'Schreib dein Konzept mit Markdown – strukturiert, klar, professionell.' },
  { icon: '🖼️', title: 'Fotos & Referenzen', desc: 'Füge Referenzbilder direkt zwischen die Szenen ein.' },
  { icon: '🔗', title: 'Kunden-Link', desc: 'Jedes Projekt bekommt einen einzigartigen Link – kein Login nötig.' },
  { icon: '💬', title: 'Zeitstempel-Feedback', desc: 'Kunden hinterlassen Anmerkungen mit Sekunde – du siehst alles in Rot.' },
  { icon: '🎬', title: 'Google Drive', desc: 'Füge das fertige Video per Drive-Link ein, direkt in der Kunden-Ansicht.' },
  { icon: '☁️', title: 'Cloud-Speicher', desc: 'Alle Daten werden sicher in Firebase gespeichert – von überall abrufbar.' },
];

const faqs = [
  { q: 'Kann ich FrameIt kostenlos testen?', a: 'Ja – jedes Abo startet mit 7 Tagen kostenloser Testphase. Keine Kreditkarte vorab nötig... warte, doch, aber du wirst die ersten 7 Tage nicht belastet.' },
  { q: 'Wie viele Projekte kann ich anlegen?', a: 'Unbegrenzt. Erstell so viele Video-Projekte wie du möchtest.' },
  { q: 'Müssen sich meine Kunden registrieren?', a: 'Nein. Du schickst einen Link – dein Kunde öffnet ihn und kann sofort Feedback hinterlassen.' },
  { q: 'Kann ich jederzeit kündigen?', a: 'Ja, jederzeit. Du kannst dein Abo über das Stripe-Kundenportal selbst verwalten.' },
  { q: 'Gibt es eine jährliche Ersparnis?', a: 'Ja – das Jahresabo kostet 99,99 € statt 119,88 € (12 × 9,99 €). Du sparst knapp 17%.' },
];

export default function LandingPage({ onLogin }) {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* ── Nav ── */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-gray-100 px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-indigo-600 rounded-lg flex items-center justify-center">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 2L13 8L3 14V2Z" fill="white"/></svg>
          </div>
          <span className="font-semibold text-sm tracking-tight">FrameIt</span>
        </div>
        <div className="hidden md:flex items-center gap-6 text-sm text-gray-500">
          <a href="#features" className="hover:text-gray-900 transition-colors">Features</a>
          <a href="#pricing" className="hover:text-gray-900 transition-colors">Preise</a>
          <a href="#faq" className="hover:text-gray-900 transition-colors">FAQ</a>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onLogin} className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 transition-colors">Anmelden</button>
          <button onClick={onLogin} className="text-sm font-medium bg-indigo-600 text-white px-4 py-1.5 rounded-xl hover:bg-indigo-700 transition-all">Kostenlos testen</button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="max-w-5xl mx-auto px-6 pt-20 pb-24 text-center">
        <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-600 text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
          <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-pulse" />
          7 Tage kostenlos testen
        </div>
        <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight tracking-tight mb-6">
          Video-Planung,<br />
          <span className="text-indigo-600">die Kunden lieben.</span>
        </h1>
        <p className="text-xl text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed">
          FrameIt ist das Tool für Video-Creator und Agenturen. Skripte schreiben, Referenzen sammeln, Kunden-Feedback einsammeln – alles an einem Ort.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <button
            onClick={onLogin}
            className="inline-flex items-center gap-2 bg-indigo-600 text-white text-sm font-semibold px-6 py-3 rounded-2xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200"
          >
            Jetzt kostenlos starten
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7H12M8 3L12 7L8 11" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <a href="#features" className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
            Mehr erfahren ↓
          </a>
        </div>

        {/* App preview */}
        <div className="mt-16 rounded-3xl border border-gray-100 shadow-2xl shadow-gray-100 overflow-hidden bg-gray-50 aspect-video flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-indigo-100 rounded-2xl mx-auto flex items-center justify-center mb-4">
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><path d="M5 3.5L23 14L5 24.5V3.5Z" fill="#6366f1"/></svg>
            </div>
            <p className="text-gray-400 text-sm">App-Screenshot</p>
            <button onClick={onLogin} className="mt-3 text-xs text-indigo-500 hover:text-indigo-700 font-medium">
              Live ausprobieren →
            </button>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="bg-gray-50 py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-widest mb-3">Features</p>
            <h2 className="text-3xl font-bold text-gray-900">Alles was du brauchst</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto">Kein Chaos mehr mit PDFs, WhatsApp-Nachrichten und Google Docs. FrameIt hält alles zusammen.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all">
                <div className="text-2xl mb-3">{f.icon}</div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-widest mb-3">Preise</p>
            <h2 className="text-3xl font-bold text-gray-900">Einfach. Transparent.</h2>
            <p className="text-gray-500 mt-3">7 Tage kostenlos testen – danach wählst du deinen Plan.</p>
          </div>

          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-3 mb-10">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`text-sm font-medium px-4 py-2 rounded-xl transition-all ${billingCycle === 'monthly' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}
            >Monatlich</button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`text-sm font-medium px-4 py-2 rounded-xl transition-all flex items-center gap-2 ${billingCycle === 'yearly' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Jährlich
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${billingCycle === 'yearly' ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-700'}`}>–17%</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {/* Monthly */}
            <div className={`rounded-3xl border-2 p-8 transition-all ${billingCycle === 'monthly' ? 'border-indigo-600 shadow-xl shadow-indigo-100' : 'border-gray-100'}`}>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Monatlich</p>
              <div className="flex items-end gap-1 mb-1">
                <span className="text-4xl font-bold text-gray-900">9,99</span>
                <span className="text-gray-400 mb-1">€/Monat</span>
              </div>
              <p className="text-xs text-gray-400 mb-6">Jederzeit kündbar</p>
              <ul className="space-y-3 mb-8 text-sm text-gray-600">
                {['Unbegrenzte Projekte', 'Kunden-Links', 'Zeitstempel-Feedback', 'Cloud-Speicher', '7 Tage kostenlos'].map(f => (
                  <li key={f} className="flex items-center gap-2">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7L5.5 10.5L12 3.5" stroke="#6366f1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    {f}
                  </li>
                ))}
              </ul>
              <CheckoutButton priceId={MONTHLY_PRICE_ID} variant={billingCycle === 'monthly' ? 'primary' : 'secondary'}>
                7 Tage kostenlos starten
              </CheckoutButton>
            </div>

            {/* Yearly */}
            <div className={`rounded-3xl border-2 p-8 transition-all relative ${billingCycle === 'yearly' ? 'border-indigo-600 shadow-xl shadow-indigo-100' : 'border-gray-100'}`}>
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                Beliebtester Plan
              </div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Jährlich</p>
              <div className="flex items-end gap-1 mb-1">
                <span className="text-4xl font-bold text-gray-900">99,99</span>
                <span className="text-gray-400 mb-1">€/Jahr</span>
              </div>
              <p className="text-xs text-gray-400 mb-6">= 8,33 € pro Monat · spare 20 €</p>
              <ul className="space-y-3 mb-8 text-sm text-gray-600">
                {['Unbegrenzte Projekte', 'Kunden-Links', 'Zeitstempel-Feedback', 'Cloud-Speicher', '7 Tage kostenlos', 'Prioritäts-Support'].map(f => (
                  <li key={f} className="flex items-center gap-2">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7L5.5 10.5L12 3.5" stroke="#10b981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    {f}
                  </li>
                ))}
              </ul>
              <CheckoutButton priceId={YEARLY_PRICE_ID} variant={billingCycle === 'yearly' ? 'primary' : 'secondary'}>
                7 Tage kostenlos starten
              </CheckoutButton>
            </div>
          </div>

          <p className="text-center text-xs text-gray-400 mt-6">
            Sichere Zahlung via Stripe · Keine versteckten Kosten · Jederzeit kündbar
          </p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="bg-gray-50 py-24">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-widest mb-3">FAQ</p>
            <h2 className="text-3xl font-bold text-gray-900">Häufige Fragen</h2>
          </div>
          <div className="flex flex-col gap-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full text-left px-6 py-4 flex items-center justify-between text-sm font-medium text-gray-800 hover:text-indigo-600 transition-colors"
                >
                  {faq.q}
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className={`shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}>
                    <path d="M2 5L7 10L12 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4 text-sm text-gray-500 leading-relaxed border-t border-gray-50 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-24">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Bereit loszulegen?</h2>
          <p className="text-gray-500 mb-8">Keine Kreditkarte nötig für den Start. 7 Tage kostenlos – danach 9,99 €/Monat.</p>
          <button
            onClick={onLogin}
            className="inline-flex items-center gap-2 bg-indigo-600 text-white text-sm font-semibold px-8 py-3.5 rounded-2xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200"
          >
            Kostenlos starten
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7H12M8 3L12 7L8 11" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-gray-100 py-8 px-6 text-center text-xs text-gray-400">
        <div className="flex items-center justify-center gap-2 mb-3">
          <div className="w-5 h-5 bg-indigo-600 rounded-md flex items-center justify-center">
            <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M2 1.5L10 6L2 10.5V1.5Z" fill="white"/></svg>
          </div>
          <span className="font-semibold text-gray-600 text-sm">FrameIt</span>
        </div>
        <p>© {new Date().getFullYear()} FrameIt · Alle Rechte vorbehalten</p>
      </footer>
    </div>
  );
}
